// Поле календарной даты.
//
// ## Почему поле СВОЁ, а не браузерное
//
// До 25.08.2026 здесь стоял `<input type="date">` с расчётом на то, что браузер сам покажет
// дату так, как принято в стране человека. Замер показал, что это неправда, и неправда
// дорогая: вид поля идёт за языком ИНТЕРФЕЙСА браузера, а не за языком страницы.
//
//   язык браузера русский      → «25/08/2026», подсказка «dd/mm/yyyy»
//   язык браузера английский   → «08/25/2026», подсказка «mm/dd/yyyy»   ← МЕСЯЦ ВПЕРЕДИ ДНЯ
//
// Атрибут `lang="ru"` на поле и на странице не меняет ничего — проверено все три варианта.
//
// То есть на машине заказчика с английской системой (а десктоп мы поставляем обёрткой над
// системным движком, и его язык нам не подчиняется) финансовый директор, вводя «05.08» —
// пятое августа, — получил бы пятое МАЯ. Это дата выдачи денег, дата назначения на объект и
// дата действия исполнительного документа. Ошибка молчаливая: поле выглядит исправным.
//
// Прежний комментарий здесь предупреждал ровно об обратном: «никакого разбора руками, каждая
// своя переделка формата — это место, где день и месяц однажды поменяются местами». Опасение
// верное, поэтому разбор закрыт проверками, включая случай перестановки: «05.08.2026» обязано
// давать август, а не май.
//
// ## Что снаружи
//
// Значение ходит и хранится ISO-строкой «2026-08-15» — ровно так его ждёт сервер, и ровно так
// его отдаёт. Меняется только показ и ввод. Ни одно место вызова править не пришлось.
//
// Календаря-выпадашки нет намеренно: за этим полем сидит финансовый отдел с клавиатурой, и
// восемь цифр набираются быстрее, чем открывается и пролистывается календарь. Появится —
// добавим кнопкой рядом, а не подменой формата.

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { RiErrorWarningLine } from '@remixicon/react';

import * as Hint from '@/shared/ui/hint';
import * as Input from '@/shared/ui/input';
import * as Label from '@/shared/ui/label';

/** Сегодня в виде «2026-08-25». Своей сборкой, а не через `toISOString`: тот отдаёт UTC, и
 *  вечером в Екатеринбурге подставил бы вчерашний день. */
export function todayIso(сейчас: Date = new Date()): string {
  return [
    String(сейчас.getFullYear()).padStart(4, '0'),
    String(сейчас.getMonth() + 1).padStart(2, '0'),
    String(сейчас.getDate()).padStart(2, '0'),
  ].join('-');
}

/** «2026-08-25» → «25.08.2026». Не ISO — пусто. */
export function isoВПоказ(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m === null ? '' : `${m[3]}.${m[2]}.${m[1]}`;
}

/**
 * Что человек набрал → что показывается.
 *
 * Точки ставит поле, а не человек: на цифровой клавиатуре точка есть не везде, а сбитый
 * разделитель превращает дату в мусор ровно в тот момент, когда её вводят быстро.
 * Принимаем и то, чем люди разделяют на самом деле: точку, косую черту, дефис, пробел.
 */
export function показПоВводу(сырое: string): string {
  const цифры = сырое.replace(/\D/g, '').slice(0, 8);
  if (цифры.length <= 2) return цифры;
  if (цифры.length <= 4) return `${цифры.slice(0, 2)}.${цифры.slice(2)}`;
  return `${цифры.slice(0, 2)}.${цифры.slice(2, 4)}.${цифры.slice(4)}`;
}

/**
 * «25.08.2026» → «2026-08-25». Неполное или несуществующее — пусто.
 *
 * Проверяем не диапазоны по отдельности, а собранную дату: 31.02 проходит любую проверку
 * «день от 1 до 31, месяц от 1 до 12» и при этом не существует.
 */
export function показВIso(показ: string): string {
  const цифры = показ.replace(/\D/g, '');
  if (цифры.length !== 8) return '';
  const день = Number(цифры.slice(0, 2));
  const месяц = Number(цифры.slice(2, 4));
  const год = Number(цифры.slice(4, 8));
  if (год < 1900 || год > 2200 || месяц < 1 || месяц > 12 || день < 1) return '';
  const проверка = new Date(год, месяц - 1, день);
  if (
    проверка.getFullYear() !== год ||
    проверка.getMonth() !== месяц - 1 ||
    проверка.getDate() !== день
  ) {
    return '';
  }
  return `${String(год).padStart(4, '0')}-${String(месяц).padStart(2, '0')}-${String(день).padStart(2, '0')}`;
}

export interface DateFieldProps {
  id: string;
  label: string;
  required?: boolean;
  /** ISO «2026-08-15» либо пустая строка. */
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: ReactNode;
}

export function DateField({ id, label, required, value, onChange, error, hint }: DateFieldProps) {
  const [показ, setПоказ] = useState(() => isoВПоказ(value));
  // Что мы сами в последний раз отдали наружу. Нужно, чтобы не затирать НЕДОНАБРАННУЮ дату:
  // пока цифр меньше восьми, наружу уходит пустое значение, и без этой памяти поле
  // сбрасывалось бы на каждой второй набранной цифре.
  const своё = useRef(value);

  useEffect(() => {
    if (value !== своё.current) {
      своё.current = value;
      setПоказ(isoВПоказ(value));
    }
  }, [value]);

  const набранаНеверно = показ.replace(/\D/g, '').length === 8 && показВIso(показ) === '';

  function ввод(сырое: string) {
    const следующий = показПоВводу(сырое);
    setПоказ(следующий);
    const iso = показВIso(следующий);
    своё.current = iso;
    onChange(iso);
  }

  const сообщение =
    error ?? (набранаНеверно ? 'Такой даты нет — проверьте день и месяц' : undefined);

  return (
    <div className='flex flex-col gap-1'>
      <Label.Root htmlFor={id}>
        {label}
        {required === true && <Label.Asterisk />}
      </Label.Root>
      <Input.Root size='small' hasError={сообщение !== undefined}>
        <Input.Wrapper>
          <Input.Input
            id={id}
            type='text'
            inputMode='numeric'
            autoComplete='off'
            placeholder='дд.мм.гггг'
            maxLength={10}
            value={показ}
            onChange={(event) => ввод(event.target.value)}
            aria-invalid={сообщение !== undefined}
          />
        </Input.Wrapper>
      </Input.Root>
      {сообщение !== undefined ? (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {сообщение}
        </Hint.Root>
      ) : (
        hint !== undefined && <Hint.Root>{hint}</Hint.Root>
      )}
    </div>
  );
}
